import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICallStage } from '../../../schemas/apis/interface';

export interface StepStatus {
  isComplete: boolean;
  hasError: boolean;
}

export type selectedActionData =
  | 'api'
  | 'transfer'
  | 'sms'
  | 'email'
  | 'schedule'
  | 'take-message';

export interface templateData {
  templateId: string;
  templateName: string;
  status: string;
}

interface UserJourneyState {
  assistantDefinition: {
    data: {
      assistantName: string;
      personality: string;
      language: string;
      voice: {
        name: string;
        _id: string;
        provider: string;
        accent: string;
      };
      timezone: string;
      agentChannel: 'whatsapp' | 'voice';
    };
    status: StepStatus;
  };
  menuConfig: {
    data: {
      method: string;
      menuSettingMethod: string;
      tmbillposId: string;
      posName: string;
      businessId: string;
      environment: string;
      posConfig: {
        apikey: string;
        endpoint: string;
      };
      menuSettingDetails: {
        storeId: number;
        refId?: string;
        tmBillBrandShortName?: string;
      }[];
      assistantId: string;
      websiteUrl?: string;
    };
    status: StepStatus;
    isAccessRequested: boolean;
    isMenuGenerationStarted: boolean;
    hasUnsavedChanges: boolean;
  };
  hotelConfig: {
    data: {
      hotelId: string;
      apiUsername: string;
      counterId: string;
      consumerKey: string;
      consumerSecret: string;
      accessSecret: string;
      baseUrl: string;
      redisHost: string;
      redisPort: number;
      isEnabled: boolean;
    };
    status: StepStatus;
    hasUnsavedChanges: boolean;
  };
  templates: templateData[];
  personalization: {
    data: {
      greeting: string;
      identity: string;
      job: string;
      selectedActions?: selectedActionData[];
    };
    status: StepStatus;
  };
  businessDetails: {
    data: {
      businessName: string;
      businessLocation: string;
      contactInfo: string;
      associatedBrands: string[];
    };
    status: StepStatus;
  };
  storeDetails: {
    data: {
      location: string;
      operatingHours: string;
      contact: string;
    };
    status: StepStatus;
  };
  errors: Record<string, string>;
  isProfileComplete: boolean;
  callStages: {
    data: ICallStage[];
    status: StepStatus;
  };
}

const initialState: UserJourneyState = {
  assistantDefinition: {
    data: {
      assistantName: '',
      personality: 'Professional',
      language: '',
      voice: {
        name: '',
        _id: '',
        provider: '',
        accent: '',
      },
      timezone: 'America/Chicago',
      agentChannel: 'voice',
    },
    status: { isComplete: false, hasError: false },
  },
  templates: [],
  menuConfig: {
    data: {
      method: '',
      menuSettingMethod: '',
      tmbillposId: '',
      posName: '',
      businessId: '',
      environment: 'stage',
      posConfig: {
        apikey: '',
        endpoint: '',
      },
      menuSettingDetails: [],
      assistantId: '',
      websiteUrl: '',
    },
    status: { isComplete: false, hasError: false },
    isAccessRequested: false,
    isMenuGenerationStarted: false,
    hasUnsavedChanges: false,
  },
  hotelConfig: {
    data: {
      hotelId: '',
      apiUsername: '',
      counterId: '',
      consumerKey: '',
      consumerSecret: '',
      accessSecret: '',
      baseUrl: 'https://crs.hotelogix.net/ws/web/',
      redisHost: 'localhost',
      redisPort: 6379,
      isEnabled: true,
    },
    status: { isComplete: false, hasError: false },
    hasUnsavedChanges: false,
  },
  personalization: {
    data: { greeting: '', identity: '', job: '', selectedActions: [] },
    status: { isComplete: false, hasError: false },
  },
  businessDetails: {
    data: {
      businessName: '',
      businessLocation: '',
      contactInfo: '',
      associatedBrands: [],
    },
    status: { isComplete: false, hasError: false },
  },
  storeDetails: {
    data: {
      location: '',
      operatingHours: '',
      contact: '',
    },
    status: { isComplete: false, hasError: false },
  },
  errors: {},
  isProfileComplete: false,
  callStages: {
    data: [],
    status: {
      isComplete: false,
      hasError: false,
    },
  },
};

const userJourneySlice = createSlice({
  name: 'userJourney',
  initialState,
  reducers: {
    updateAssistantDefinition: (state, action) => {
      state.assistantDefinition.data = {
        ...state.assistantDefinition.data,
        ...action.payload,
      };
    },
    updateMenuConfig: (state, action) => {
      state.menuConfig.data = {
        ...state.menuConfig.data,
        ...action.payload,
      };
    },
    updateHotelConfig: (state, action) => {
      state.hotelConfig.data = {
        ...state.hotelConfig.data,
        ...action.payload,
      };
    },
    updatePersonalization: (state, action) => {
      state.personalization.data = {
        ...state.personalization.data,
        ...action.payload,
      };
    },
    updateBusinessDetails: (state, action) => {
      state.businessDetails.data = {
        ...state.businessDetails.data,
        ...action.payload,
      };
    },
    addAssociatedBrand: (state, action: PayloadAction<string>) => {
      state.businessDetails.data.associatedBrands.push(action.payload);
    },
    removeAssociatedBrand: (state, action: PayloadAction<string>) => {
      state.businessDetails.data.associatedBrands =
        state.businessDetails.data.associatedBrands.filter(
          brand => brand !== action.payload
        );
    },
    setStepStatus: (
      state,
      action: PayloadAction<{
        step:
          | 'assistantDefinition'
          | 'menuConfig'
          | 'hotelConfig'
          | 'personalization'
          | 'businessDetails'
          | 'storeDetails'
          | 'callStages';
        status: StepStatus;
      }>
    ) => {
      const { step, status } = action.payload;
      if (typeof state[step] === 'object') {
        (state[step] as { status: StepStatus }).status = status;
      }
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    clearErrors: state => {
      state.errors = {};
    },
    setProfileComplete: (state, action: PayloadAction<boolean>) => {
      state.isProfileComplete = action.payload;
    },
    setIsAccessRequested: (state, action: PayloadAction<boolean>) => {
      state.menuConfig.isAccessRequested = action.payload;
    },
    setIsMenuGenerationStarted: (state, action: PayloadAction<boolean>) => {
      state.menuConfig.isMenuGenerationStarted = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.menuConfig.hasUnsavedChanges = action.payload;
    },
    setHotelConfigUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hotelConfig.hasUnsavedChanges = action.payload;
    },
    setTemplates: (state, action: PayloadAction<templateData[]>) => {
      state.templates = action.payload;
    },
    updateCallStages: (state, action: PayloadAction<ICallStage[]>) => {
      state.callStages.data = action.payload;
    },
    clearAllState: state => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  updateAssistantDefinition,
  updateMenuConfig,
  updateHotelConfig,
  updatePersonalization,
  updateBusinessDetails,
  addAssociatedBrand,
  removeAssociatedBrand,
  setStepStatus,
  setErrors,
  clearErrors,
  setProfileComplete,
  setIsAccessRequested,
  setIsMenuGenerationStarted,
  setTemplates,
  updateCallStages,
  clearAllState,
  setHasUnsavedChanges,
  setHotelConfigUnsavedChanges,
} = userJourneySlice.actions;

export default userJourneySlice.reducer;
